import {
  GoogleAuthProvider,
  OAuthProvider,
  signInWithRedirect,
  getRedirectResult,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signOut as firebaseSignOut,
  updateProfile,
  onAuthStateChanged,
  type User,
} from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "@/lib/firebase";
import type { UserProfile, AuthProvider } from "@/types";

// ─── TYPES ───────────────────────────────────────────────────────────────────
export interface AuthResult {
  ok:   true;
  user: User;
}

export interface AuthFailure {
  ok:      false;
  code:    string;
  message: string;
}

export type AuthResponse = AuthResult | AuthFailure;

// ─── ERROR MAPPING ────────────────────────────────────────────────────────────
const AUTH_ERRORS: Record<string, string> = {
  // Sign-in failures
  "auth/user-not-found":          "No account found with this email.",
  "auth/wrong-password":          "Incorrect password. Please try again.",
  "auth/invalid-credential":      "Incorrect email or password. Please try again.",
  "auth/invalid-email":           "Please enter a valid email address.",
  "auth/missing-email":           "Please enter your email address.",
  "auth/missing-password":        "Please enter your password.",

  // Account issues
  "auth/email-already-in-use":    "An account with this email already exists.",
  "auth/weak-password":           "Password must be at least 6 characters.",
  "auth/user-disabled":           "This account has been disabled. Please contact support.",
  "auth/account-exists-with-different-credential":
    "An account already exists with this email via a different sign-in method.",

  // Provider not configured in Firebase Console
  "auth/operation-not-allowed":
    "This sign-in method is not enabled. Please use Google or email/password.",

  // Popup / OAuth flow
  "auth/popup-closed-by-user":    "Sign-in was cancelled.",
  "auth/cancelled-popup-request": "Sign-in was cancelled.",
  "auth/popup-blocked":
    "Sign-in popup was blocked. Please allow popups for this site and try again.",

  // Magic link
  "auth/invalid-action-code":     "This sign-in link is invalid or has already been used.",
  "auth/expired-action-code":     "This sign-in link has expired. Please request a new one.",

  // Network / other
  "auth/network-request-failed":  "Network error. Please check your connection and try again.",
  "auth/too-many-requests":       "Too many attempts. Please wait a moment and try again.",
  "auth/requires-recent-login":   "Please sign in again to continue.",
};

function mapError(code: string): AuthFailure {
  return {
    ok:      false,
    code,
    message: AUTH_ERRORS[code] ?? "Something went wrong. Please try again.",
  };
}

function handleAuthError(err: unknown): AuthFailure {
  const code =
    err instanceof Error && "code" in err
      ? String((err as { code: string }).code)
      : "unknown";
  return mapError(code);
}

// ─── PROFILE HELPERS ─────────────────────────────────────────────────────────
function getInitials(name: string): string {
  return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
}

async function syncUserProfile(user: User, provider: AuthProvider): Promise<void> {
  const ref      = doc(db, "users", user.uid);
  const existing = await getDoc(ref);

  if (!existing.exists()) {
    const profile: Omit<UserProfile, "location" | "phoneNumber"> = {
      uid:                 user.uid,
      displayName:         user.displayName ?? "AdhviBooks Reader",
      email:               user.email ?? "",
      photoURL:            user.photoURL,
      initials:            getInitials(user.displayName ?? "AB"),
      authProviders:       [provider],
      reminderPreferences: ["email"],
      borrowCount:         0,
      shareCount:          0,
      averageRating:       0,
      badges:              [],
      createdAt:           new Date(),
    };
    await setDoc(ref, { ...profile, createdAt: serverTimestamp() });
  } else {
    const data      = existing.data() as UserProfile;
    const providers = data.authProviders ?? [];
    if (!providers.includes(provider)) {
      await setDoc(ref, {
        authProviders: [...providers, provider],
        displayName:   user.displayName ?? data.displayName,
        photoURL:      user.photoURL    ?? data.photoURL,
      }, { merge: true });
    }
  }
}

// ─── SOCIAL SIGN-IN (redirect flow) ──────────────────────────────────────────
// Uses redirect instead of popup — more reliable across browsers/devices.
// Call signInWithGoogle/Apple to start; handleRedirectResult on app boot to finish.

export const signInWithGoogle = (): void => {
  const p = new GoogleAuthProvider();
  p.addScope("email");
  p.addScope("profile");
  signInWithRedirect(auth, p);
};

export const signInWithApple = (): void => {
  const p = new OAuthProvider("apple.com");
  p.addScope("email");
  p.addScope("name");
  signInWithRedirect(auth, p);
};

export async function handleRedirectResult(): Promise<AuthResponse | null> {
  try {
    const result = await getRedirectResult(auth);
    if (!result) return null;
    const providerMap: Record<string, AuthProvider> = {
      "google.com": "google",
      "apple.com":  "apple",
    };
    const provider = providerMap[result.providerId ?? ""] ?? "email";
    await syncUserProfile(result.user, provider);
    return { ok: true, user: result.user };
  } catch (err) {
    return handleAuthError(err);
  }
}

// ─── EMAIL + PASSWORD ─────────────────────────────────────────────────────────
export async function signInWithEmail(
  email: string,
  password: string,
): Promise<AuthResponse> {
  try {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    return { ok: true, user: cred.user };
  } catch (err) {
    return handleAuthError(err);
  }
}

export async function signUpWithEmail(
  email: string,
  password: string,
  displayName: string,
): Promise<AuthResponse> {
  try {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(cred.user, { displayName });

    // Send verification email — non-blocking: don't fail sign-up if this errors
    try { await sendEmailVerification(cred.user); } catch { /* ignore */ }

    await syncUserProfile(cred.user, "email");
    return { ok: true, user: cred.user };
  } catch (err) {
    return handleAuthError(err);
  }
}

export async function resetPassword(
  email: string,
): Promise<{ ok: boolean; message: string }> {
  try {
    await sendPasswordResetEmail(auth, email);
    return { ok: true, message: "Password reset email sent! Check your inbox." };
  } catch (err) {
    const f = handleAuthError(err);
    return { ok: false, message: f.message };
  }
}

// ─── MAGIC LINK ───────────────────────────────────────────────────────────────
const MAGIC_LINK_KEY     = "adhvi_magic_link_email";
const MAGIC_LINK_COOLDOWN = 60_000;
let lastMagicLinkSent    = 0;

export async function sendMagicLink(
  email: string,
): Promise<{ ok: boolean; message: string }> {
  const now = Date.now();
  if (now - lastMagicLinkSent < MAGIC_LINK_COOLDOWN) {
    const remaining = Math.ceil((MAGIC_LINK_COOLDOWN - (now - lastMagicLinkSent)) / 1000);
    return { ok: false, message: `Please wait ${remaining}s before sending another link.` };
  }
  try {
    await sendSignInLinkToEmail(auth, email, {
      url:             `${window.location.origin}/auth/callback`,
      handleCodeInApp: true,
    });
    localStorage.setItem(MAGIC_LINK_KEY, email);
    lastMagicLinkSent = Date.now();
    return { ok: true, message: "Magic link sent! Check your inbox and click the link." };
  } catch (err) {
    return { ok: false, message: handleAuthError(err).message };
  }
}

export async function completeMagicLinkSignIn(url: string): Promise<AuthResponse> {
  if (!isSignInWithEmailLink(auth, url)) {
    return { ok: false, code: "invalid-link", message: "Invalid sign-in link." };
  }
  let email = localStorage.getItem(MAGIC_LINK_KEY);
  if (!email) {
    email = window.prompt("Please enter your email to confirm sign-in:") ?? "";
  }
  try {
    const cred = await signInWithEmailLink(auth, email, url);
    localStorage.removeItem(MAGIC_LINK_KEY);
    await syncUserProfile(cred.user, "magic");
    return { ok: true, user: cred.user };
  } catch (err) {
    return handleAuthError(err);
  }
}

// ─── SIGN OUT ────────────────────────────────────────────────────────────────
export async function signOut(): Promise<void> {
  await firebaseSignOut(auth);
}

// ─── AUTH STATE OBSERVER ─────────────────────────────────────────────────────
export function subscribeToAuthState(callback: (user: User | null) => void): () => void {
  return onAuthStateChanged(auth, callback);
}

export function getCurrentUser(): Promise<User | null> {
  return new Promise((resolve) => {
    const unsub = onAuthStateChanged(auth, (user) => { unsub(); resolve(user); });
  });
}
